import * as React from 'react';
import {
  type DropEvent,
  type DropzoneOptions,
  type FileError,
  useDropzone,
} from 'react-dropzone';
import clsx from 'clsx';
import {
  styled,
  ButtonBase as MuiButtonBase,
  type ButtonBaseProps as MuiButtonBaseProps,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import useId from '@mui/utils/useId';
import { getToken } from '@workiva/unify-tokens';

import { Typography } from 'src/components/Typography';
import * as UnifyIcons from 'src/components/UnifyIcons';
import {
  buildDefaultFileModelFromFileOrFileRejection,
  type FileModel,
} from 'src/components/FileUploader/fileModel';
import fileUploaderClasses from 'src/components/FileUploader/fileUploaderClasses';
import type { FileUploaderProps } from 'src/components/FileUploader/FileUploader';
import { getDefaultFileItemErrorMessage } from 'src/components/FileUploader/fileUploaderDefaultErrorMessages';

export const fileUploaderMinWidth = 200;

/**
 * @access package
 */
export type FileUploaderDropzoneOwnProps = {
  /**
   * @param fileModels File models containing error messaging that has been updated with Unify defaults
   * @param event
   *
   * @access package
   */
  onChange: (fileModels: FileModel[], event?: DropEvent) => void;

  /**
   * The size of the dropzone for the `FileUploader`.
   *
   * @default 'medium'
   */
  size?: 'small' | 'medium';
};

type FileUploaderDropzoneProps = Omit<
  React.HTMLAttributes<HTMLButtonElement>,
  'onChange'
> &
  Required<
    Pick<
      FileUploaderProps,
      'disabled' | 'acceptedMimeTypes' | 'maxNumberOfFiles'
    >
  > &
  Pick<FileUploaderProps, 'minSizeInBytes' | 'maxSizeInBytes'> &
  Pick<MuiButtonBaseProps, 'sx'> &
  FileUploaderDropzoneOwnProps;

// eslint-disable-next-line deprecation/deprecation
const FileUploaderDropzoneRoot = styled(MuiButtonBase, {
  name: 'UnifyFileUploader',
  slot: 'Dropzone',
  overridesResolver: (_, styles) => {
    return [
      styles.dropzone,
      {
        [`&.${fileUploaderClasses.dropzoneFocused}`]: styles.dropzoneFocused,
      },
      {
        [`&.${fileUploaderClasses.dropzoneDragActive}`]:
          styles.dropzoneDragActive,
      },
      {
        [`&.${fileUploaderClasses.dropzoneDragAccept}`]:
          styles.dropzoneDragAccept,
      },
      {
        [`&.${fileUploaderClasses.dropzoneDragReject}`]:
          styles.dropzoneDragReject,
      },
    ];
  },
})<FileUploaderDropzoneDropzoneProps>(({ disabled, ownerState, theme }) => {
  const { isDragActive, size } = ownerState;
  return {
    borderRadius: theme.shape.borderRadius,
    padding: size === 'small' ? theme.spacing(4) : theme.spacing(8, 4),
    minWidth: fileUploaderMinWidth,
    width: '100%',
    textAlign: 'center',
    display: 'inline-block',
    verticalAlign: 'middle',
    backgroundColor: getToken('Component/Dropzone/dropzone-background-default'),
    // We're using outline so the height doesn't change onDragActive when the outline gets thicker,
    // but since we need a total of 2px of border height to ensure that whole spacing units can be
    // used for padding and still match the computed height that the Figma component has - we set
    // a 1px transparent border in addition to the visible outline.
    border: '1px solid transparent',
    outline: `1px solid ${getToken(
      'Component/Dropzone/dropzone-border-default'
    )}`,
    // Use a negative offset equal to the outline width to ensure the outline stays within the intrinsic box model (e.g. "inset" border)
    outlineOffset: '-1px',

    ...(!isDragActive && {
      '&:hover': {
        backgroundColor: getToken(
          'Component/Dropzone/dropzone-background-hover'
        ),
        outlineColor: getToken('Component/Dropzone/dropzone-border-hover'),
      },
    }),

    [`&.${fileUploaderClasses.dropzoneFocused}`]: {
      outlineColor: getToken('Component/Dropzone/dropzone-border-dragover'),
      outline: `2px solid ${getToken(
        'Component/Dropzone/dropzone-border-dragover'
      )}`,
      outlineOffset: '-2px',
    },

    [`&.${fileUploaderClasses.dropzoneDragActive}`]: {
      backgroundColor: getToken(
        'Component/Dropzone/dropzone-background-dragover'
      ),
      outlineColor: getToken('Component/Dropzone/dropzone-border-dragover'),
      outlineWidth: 2,
      // Use a negative offset equal to the outline width to ensure the outline stays within the intrinsic box model (e.g. "inset" border)
      outlineOffset: '-2px',
    },

    ...(disabled && {
      backgroundColor: getToken(
        'Component/Dropzone/dropzone-background-disabled'
      ),
      outlineColor: getToken('Component/Dropzone/dropzone-border-disabled'),
    }),
  };
});

// eslint-disable-next-line deprecation/deprecation
type FileUploaderDropzoneDropzoneProps = Omit<
  MuiButtonBaseProps,
  'disabled'
> & {
  disabled: boolean;
} & {
  ownerState: {
    isDragActive: boolean;
    size: FileUploaderDropzoneOwnProps['size'];
  };
};

export const FileUploaderDropzone = React.memo(function FileUploaderDropzone(
  props: FileUploaderDropzoneProps
) {
  const {
    id: idProp,
    className: classNameProp,
    disabled,
    onChange,
    acceptedMimeTypes: accept,
    minSizeInBytes: minSize,
    maxSizeInBytes: maxSize,
    maxNumberOfFiles: maxFiles = 0,
    size = 'medium',
    ...rest
  } = props;
  const id = useId(idProp);

  // If single file upload, force the dropzone to be small
  const finalSize = maxFiles === 1 ? 'small' : size;

  const handleDrop = React.useCallback<NonNullable<DropzoneOptions['onDrop']>>(
    (acceptedFiles, fileRejections, event) => {
      const allFiles = [...acceptedFiles, ...fileRejections];
      const fileModels = allFiles
        .map(buildDefaultFileModelFromFileOrFileRejection)
        .map((model) => {
          const defaultErrors = model.errors
            ?.map((err) => {
              // Update the error messages to be our default wording instead
              // of the wording from react-dropzone.
              const defaultMessage = getDefaultFileItemErrorMessage({
                errorCode: err.code,
                acceptedMimeTypes: accept,
                maxNumberOfFiles: maxFiles,
                minSizeInBytes: minSize,
                maxSizeInBytes: maxSize,
              });

              return {
                code: err.code,
                // If defaultMessage is undefined, it means that this type of error message
                // should not be displayed within an individual FileItem.
                message: defaultMessage,
              };
            })
            .filter((err): err is FileError => err !== undefined);

          if (!defaultErrors) return model;

          return {
            ...model,
            errors: defaultErrors,
          };
        });

      onChange(
        // Construct a default array of file models to pass to the consumer where they can then optionally
        // add more metadata like upload progress percentage / custom error messages.
        fileModels,
        event
      );
    },
    [accept, maxFiles, maxSize, minSize, onChange]
  );

  const { getRootProps, getInputProps, ...restDropzoneState } = useDropzone({
    onDrop: handleDrop,
    disabled,
    accept,
    minSize,
    maxSize,
    maxFiles,
    multiple: maxFiles !== 1,
  });

  const { isDragActive, isDragAccept, isDragReject, isFocused } =
    restDropzoneState;

  const className = clsx(
    classNameProp,
    fileUploaderClasses.dropzone,
    isFocused && fileUploaderClasses.dropzoneFocused,
    isDragActive && fileUploaderClasses.dropzoneDragActive,
    isDragActive && isDragAccept && fileUploaderClasses.dropzoneDragAccept,
    isDragActive && isDragReject && fileUploaderClasses.dropzoneDragReject
  );

  return (
    <>
      <FileUploaderDropzoneRoot
        {...getRootProps({ className, ...rest })}
        // Override the role="presentation" that getRootProps inexplicably returns
        role={undefined}
        disableRipple
        disabled={disabled}
        ownerState={{
          isDragActive,
          size: finalSize,
        }}
      >
        <UnifyIcons.Upload
          fontSize="small"
          sx={{
            color: disabled ? undefined : getToken('Core/Icon/icon-secondary'),
            // Negative bottom margin is necessary to vertically align the icon inline
            // with the first line of text while also not creating a block context that
            // prevents the text from displaying beneath the icon when it wraps.
            marginBottom: (theme) => `${theme.typography.fontSize / -2}px`,
            paddingBottom: '22px',
          }}
        />
        <BrowseOrDragText disabled={disabled} maxNumberOfFiles={maxFiles} />
      </FileUploaderDropzoneRoot>
      <HiddenLabel htmlFor={id}>
        <BrowseOrDragText
          disabled={disabled}
          maxNumberOfFiles={maxFiles}
          disableTypography={true}
        />
      </HiddenLabel>
      <input
        {...getInputProps({
          id,
          'data-test-id': 'FileUploaderInput',
        })}
      />
    </>
  );
});

type BrowseOrDragTextProps = {
  maxNumberOfFiles: number;
  disabled: boolean;
  disableTypography?: boolean;
};

function BrowseOrDragText(props: BrowseOrDragTextProps): React.ReactElement {
  const { maxNumberOfFiles, disabled, disableTypography = false } = props;
  // FIXME (adl): intl these strings
  const browse = 'Browse';
  const orDrag = `or drag and drop ${
    maxNumberOfFiles === 1 ? 'a file' : 'files'
  } here.`;

  if (disableTypography === true) return <>{`${browse} ${orDrag}`}</>;

  return (
    // Root span wrapping around everything is necessary for text wrapping to work as expected.
    <Typography component="span">
      <Typography
        component="span"
        sx={{
          color: disabled
            ? getToken('Core/Link/link-disabled')
            : getToken('Core/Link/link-primary'),
        }}
      >
        {browse}
      </Typography>
      &nbsp;
      {orDrag}
    </Typography>
  );
}

const HiddenLabel = styled('label', {
  name: 'FileUploaderDropzone',
  slot: 'FileInputLabel',
})({ ...visuallyHidden });

export default FileUploaderDropzone;
